import React, { useState, useContext, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Bars3Icon, XMarkIcon, MoonIcon, SunIcon,
  AcademicCapIcon, HomeIcon, ClipboardDocumentListIcon, UserGroupIcon,
  BellIcon, SparklesIcon, UsersIcon, BellAlertIcon, UserCircleIcon, Cog6ToothIcon, ArrowRightOnRectangleIcon
} from '@heroicons/react/24/outline';
import ThemeContext from '../../context/ThemeContext';
import ChatDropdown from '../common/ChatDropdown';
import NotificationDropdown from '../common/NotificationDropdown';
const getNavigation = (userRole) => [
  { name: 'Ana Sayfa', href: '/', icon: HomeIcon },
  { name: 'İlanlar', href: '/courses', icon: AcademicCapIcon, badge: '50+' },
  { name: 'Eğitmenler', href: '/teachers', icon: UsersIcon },
  ...(!userRole ? [
    { name: 'Eğitmen Ol', href: '/register', icon: UserGroupIcon, special: true }
  ] : [])
];

// Banner'ın tekrar gösterilmemesi için anahtar
const CHAT_BANNER_KEY = 'chatBannerDismissed';

const Navbar = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { darkMode, toggleDarkMode } = useContext(ThemeContext);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const drawerRef = useRef(null);
  const [user, setUser] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef(null);
  const [announcementCount, setAnnouncementCount] = useState(null);

  // Chat & Bildirim Bannerı state
  const [showChatBanner, setShowChatBanner] = useState(false);

  // Kullanıcıyı localStorage'dan dinle ve güncelle
  useEffect(() => {
    const syncUser = () => {
      const userStr = localStorage.getItem('user');
      const user = userStr ? JSON.parse(userStr) : null;

      if (userStr) {
        setUser(JSON.parse(userStr));
        setIsLoggedIn(true);
      } else {
        setUser(null);
        setIsLoggedIn(false);
      }
    };

    // İlk yüklemede çağır
    syncUser();

    // Storage değişikliklerini dinle (başka sekmede login/logout olursa)
    window.addEventListener('storage', syncUser);

    // Kayıt sonrası localStorage güncellenince Navbar'ı güncellemek için custom event dinle
    window.addEventListener('user-updated', syncUser);

    return () => {
      window.removeEventListener('storage', syncUser);
      window.removeEventListener('user-updated', syncUser);
    };
  }, []);

  useEffect(() => {
    // Daha önce kapatıldıysa gösterme
    if (localStorage.getItem(CHAT_BANNER_KEY) === '1') return;
    setShowChatBanner(true);
    // 4 saniye sonra otomatik kapat
    const timeout = setTimeout(() => {
      setShowChatBanner(false);
      localStorage.setItem(CHAT_BANNER_KEY, '1');
    }, 4000);
    return () => clearTimeout(timeout);
  }, []);

  const handleCloseBanner = () => {
    setShowChatBanner(false);
    localStorage.setItem(CHAT_BANNER_KEY, '1');
  };

  const handleLogout = async () => {
    const userStr = localStorage.getItem('user');
    const user = userStr ? JSON.parse(userStr) : null;
    try {
      if (user && user.id) {
        await fetch(
          `/v1/auth/logout/${user.id}?token=`, // token param required by backend, but we don't have it anymore
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' }
          }
        );
      }
    } catch (err) {
      // Hata olsa bile devam et
    }
    localStorage.removeItem('user');
    setUser(null);
    setIsLoggedIn(false);
    navigate('/login');
  };

  // Fetch announcement count for badge
  useEffect(() => {
    const fetchAnnouncementCount = async () => {
      try {
        const res = await fetch('/v1/dashboard/announcement');
        const data = await res.json();
        if (data && data.success && typeof data.data === 'number') {
          setAnnouncementCount(data.data);
        }
      } catch {
        setAnnouncementCount(null);
      }
    };
    fetchAnnouncementCount();
  }, []);

  const navigation = getNavigation(user?.role).map(item =>
    item.href === '/courses'
      ? { ...item, badge: announcementCount !== null ? announcementCount + '+' : '' }
      : item
  );

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setUserMenuOpen(false);
  }, [location]);

  useEffect(() => {
    if (!mobileOpen) return;
    const handleClick = (e) => {
      if (drawerRef.current && !drawerRef.current.contains(e.target)) setMobileOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    document.body.style.overflow = mobileOpen ? 'hidden' : 'auto';
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.body.style.overflow = 'auto';
    };
  }, [mobileOpen]);

  useEffect(() => {
    if (!userMenuOpen) return;
    const handleClick = (e) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) setUserMenuOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [userMenuOpen]);

  const navigateToDashboard = () => {
    if (user?.role === 'ROLE_ADMIN') {
      navigate('/admin');
    } else if (user?.role === 'ROLE_TEACHER') {
      navigate('/teacher-dashboard');
    } else if (user?.role === 'ROLE_STUDENT') {
      navigate('/student-dashboard');
    } else {
      navigate('/dashboard');
    }
  };

  // Eğitmen Ol butonuna basınca register sayfasında teacher tabını açmak için
  const handleRegisterTeacher = (e) => {
    e.preventDefault();
    navigate('/register?tab=teacher');
  };

  return (
    <>
      <div className="h-16"></div>
      {/* Chat & Bildirim Bannerı - Navbar Altında Tam Genişlikte, Otomatik ve Elle Kapatılabilir */}
      {showChatBanner && (
        <div className="w-full flex justify-center items-center bg-gradient-to-r from-blue-50 to-cyan-50 dark:from-blue-900/10 dark:to-cyan-900/10 border-b border-blue-100 dark:border-blue-900/30 py-2 px-4 animate-fadeInUp relative">
          <span className="relative flex items-center gap-1">
            <svg className="w-6 h-6 text-blue-500 animate-bounce" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M21 12c0 4.418-4.03 8-9 8a9.77 9.77 0 01-4-.8L3 21l1.8-4A7.97 7.97 0 013 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
            </svg>
            <svg className="w-5 h-5 text-amber-500 animate-bounce-slow" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V4a2 2 0 10-4 0v1.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
            </svg>
          </span>
          <span className="text-xs sm:text-sm font-semibold text-blue-700 dark:text-blue-300 ml-1">Yeni:</span>
          <span className="text-xs sm:text-sm text-gray-700 dark:text-gray-200 font-medium ml-1">Artık anlık chat ve bildirim var!</span>
          <button onClick={handleCloseBanner} className="ml-3 p-1 rounded hover:bg-blue-100 dark:hover:bg-blue-900/30 transition" aria-label="Kapat">
            <svg className="w-4 h-4 text-gray-500 dark:text-gray-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <style jsx>{`
            .animate-bounce-slow {
              animation: bounce 2.2s infinite;
            }
            @keyframes fadeInUp {
              from { opacity: 0; transform: translateY(20px); }
              to { opacity: 1; transform: translateY(0); }
            }
            .animate-fadeInUp {
              animation: fadeInUp 1s ease forwards;
            }
          `}</style>
        </div>
      )}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 w-full ${
          isScrolled
            ? 'bg-white/95 backdrop-blur-md shadow-lg dark:bg-gray-900/95'
            : 'bg-white dark:bg-gray-900'
        }`}
      >
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center">
              <div className="h-10 w-10 mr-2.5 rounded-xl shadow-lg overflow-hidden flex items-center justify-center bg-white">
                <img 
                  src="/logo.png"
                  alt="DersMaster"
                  className="h-full w-full object-cover object-center scale-125"
                />
              </div>
              <span className="text-2xl font-bold text-gray-900 dark:text-white font-serif tracking-tight">
                DersMaster
              </span>
            </Link>

            <div className="hidden md:flex md:items-center md:space-x-1 lg:space-x-2">
              {navigation.map((item) =>
                item.name === 'Eğitmen Ol' ? (
                  <button
                    key={item.name}
                    onClick={handleRegisterTeacher}
                    className={`px-3 lg:px-4 py-2 rounded-xl text-sm font-medium transition-all duration-300 flex items-center space-x-1 lg:space-x-2 group relative
                      bg-gradient-to-r from-purple-500 to-blue-500 text-white hover:shadow-lg hover:shadow-purple-500/25`}
                  >
                    <item.icon className="h-5 w-5 text-white" />
                    <span className="group-hover:translate-x-0.5 transition-transform duration-200">
                      {item.name}
                    </span>
                    {item.special && (
                      <SparklesIcon className="absolute -top-1 -right-1 h-4 w-4 text-yellow-300 animate-pulse" />
                    )}
                  </button>
                ) : (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`px-3 lg:px-4 py-2 rounded-xl text-sm font-medium transition-all duration-300 flex items-center space-x-1 lg:space-x-2 group relative
                      ${item.special ? 'bg-gradient-to-r from-purple-500 to-blue-500 text-white hover:shadow-lg hover:shadow-purple-500/25' : ''}
                      ${location.pathname === item.href
                        ? 'bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-300'
                        : 'text-gray-700 hover:bg-gray-50 hover:text-blue-600 dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:text-blue-300'
                    }`}
                    aria-current={location.pathname === item.href ? 'page' : undefined}
                  >
                    <item.icon className={`h-5 w-5 transition-all duration-300 group-hover:scale-110 ${
                      location.pathname === item.href
                        ? 'text-blue-600 dark:text-blue-300'
                        : item.special ? 'text-white' : 'text-gray-500 dark:text-gray-400'
                    }`} />
                    <span className="group-hover:translate-x-0.5 transition-transform duration-200">
                      {item.name}
                    </span>
                    {item.badge && (
                      <span className="ml-1 px-1.5 py-0.5 text-xs font-semibold bg-blue-100 text-blue-600 dark:bg-blue-900/40 dark:text-blue-300 rounded-full">
                        {item.badge}
                      </span>
                    )}
                    {item.special && (
                      <SparklesIcon className="absolute -top-1 -right-1 h-4 w-4 text-yellow-300 animate-pulse" />
                    )}
                  </Link>
                )
              )}
            </div>

            <div className="hidden md:flex md:items-center md:space-x-3">
              <button
                onClick={toggleDarkMode}
                className="p-2 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-white dark:hover:bg-gray-800 transition-colors"
                aria-label={darkMode ? 'Açık Moda Geç' : 'Koyu Moda Geç'}
              >
                {darkMode ? <SunIcon className="h-5 w-5" /> : <MoonIcon className="h-5 w-5" />}
              </button>
              {isLoggedIn && <NotificationDropdown />}
              {isLoggedIn && <ChatDropdown />}
                   {isLoggedIn ? (
                <div className="relative" ref={userMenuRef}>
                  <button 
                    className="flex items-center space-x-2 p-2 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                    onClick={() => setUserMenuOpen(!userMenuOpen)}
                    aria-expanded={userMenuOpen}
                    aria-haspopup="true"
                  >
                    {user.profileImageUrl ? (
                      <img
                        src={user.profileImageUrl}
                        alt={user.username}
                        className="h-8 w-8 rounded-full object-cover border-2 border-blue-100 dark:border-blue-900"
                      />
                    ) : (
                      <div className="h-8 w-8 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
                        <UserCircleIcon className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                      </div>
                    )}
                    <span className="text-gray-700 dark:text-gray-300 font-medium hidden sm:block">{user.username}</span>
                  </button>
                  
                  <div 
                    className={`absolute right-0 mt-2 w-56 bg-white dark:bg-gray-800 rounded-xl shadow-lg py-2 transition-all duration-200 origin-top-right ${
                      userMenuOpen 
                        ? 'opacity-100 visible scale-100 translate-y-0' 
                        : 'opacity-0 invisible scale-95 -translate-y-2'
                    }`}
                  >
                    <div className="px-4 py-2 border-b border-gray-100 dark:border-gray-700">
                      <p className="text-sm font-medium text-gray-900 dark:text-white">{user.username}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {user.role === 'ROLE_ADMIN' ? 'Admin' : user.role === 'ROLE_TEACHER' ? 'Eğitmen' : 'Öğrenci'}
                      </p>
                    </div>
                    
                    <div className="py-1">
                      <Link to="/profile" className="flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700">
                        <UserCircleIcon className="h-5 w-5 mr-3 text-gray-500 dark:text-gray-400" />
                        Profilim
                      </Link>
                      <button 
                        className="flex items-center w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                        onClick={navigateToDashboard}
                      >
                        <ClipboardDocumentListIcon className="h-5 w-5 mr-3 text-gray-500 dark:text-gray-400" />
                        {user.role === 'ROLE_ADMIN' ? 'Admin Panel' : 'Dashboard'}
                      </button>
                      <Link to="/settings" className="flex items-center px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700">
                        <Cog6ToothIcon className="h-5 w-5 mr-3 text-gray-500 dark:text-gray-400" />
                        Ayarlar
                      </Link>
                    </div>
                    
                    <div className="py-1 border-t border-gray-100 dark:border-gray-700">
                      <button
                        onClick={handleLogout}
                        className="flex items-center w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-gray-100 dark:hover:bg-gray-700"
                      >
                        <ArrowRightOnRectangleIcon className="h-5 w-5 mr-3 text-red-500 dark:text-red-400" />
                        Çıkış Yap
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="flex items-center space-x-3">
                  <Link
                    to="/login"
                    className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors text-sm font-medium"
                  >
                    Giriş Yap
                  </Link>
                  <Link
                    to="/register"
                    className="px-4 py-2 bg-blue-500 hover:bg-blue-600 dark:bg-blue-600 dark:hover:bg-blue-700 text-white rounded-xl shadow-md hover:shadow-lg transition-all duration-200 transform hover:-translate-y-0.5 text-sm font-medium"
                  >
                    Kayıt Ol
                  </Link>
                </div>
              )}
            </div>

            <div className="flex items-center md:hidden space-x-2">
              {isLoggedIn && <NotificationDropdown />}
              {isLoggedIn && <ChatDropdown />}
              {isLoggedIn && (
                <button 
                  className="p-2 rounded-lg"
                  onClick={() => navigateToDashboard()}
                >
                  {user.profileImageUrl ? (
                    <img
                      src={user.profileImageUrl}
                      alt={user.username}
                      className="h-8 w-8 rounded-full object-cover border-2 border-blue-100 dark:border-blue-900"
                    />
                  ) : (
                    <div className="h-8 w-8 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
                      <UserCircleIcon className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                    </div>
                  )}
                </button>
              )}
              
              <button
                onClick={toggleDarkMode}
                className="p-2 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-white dark:hover:bg-gray-800 transition-colors"
                aria-label={darkMode ? 'Açık Moda Geç' : 'Koyu Moda Geç'}
              >
                {darkMode ? <SunIcon className="h-5 w-5" /> : <MoonIcon className="h-5 w-5" />}
              </button>
              
              <button
                onClick={() => setMobileOpen(true)}
                className="inline-flex items-center justify-center p-2 rounded-lg text-gray-500 hover:text-blue-600 hover:bg-blue-50 dark:text-gray-400 dark:hover:text-blue-300 dark:hover:bg-gray-800 transition-colors"
                aria-label="Menüyü Aç"
                aria-expanded={mobileOpen}
              >
                <Bars3Icon className="h-6 w-6" />
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      <div
        className={`fixed inset-0 z-[60] md:hidden transition-all duration-300 ${mobileOpen ? 'visible' : 'invisible pointer-events-none'}`}
      >
        <div
          className={`fixed inset-0 bg-black/60 backdrop-blur-sm transition-opacity duration-300 ${mobileOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'}`}
          onClick={() => setMobileOpen(false)}
          aria-hidden="true"
        />
        
        <div
          ref={drawerRef}
          className={`fixed top-0 right-0 h-full w-[85%] max-w-xs bg-white dark:bg-gray-900 shadow-xl transition-transform duration-300 ease-in-out ${
            mobileOpen ? 'translate-x-0' : 'translate-x-full'
          } flex flex-col overflow-y-auto`}
          role="dialog"
          aria-modal="true"
        >
          <div className="flex items-center justify-between px-4 h-16 border-b border-gray-100 dark:border-gray-800">
            <div className="flex items-center">
              <div className="h-7 w-7 mr-2 rounded-lg shadow-md overflow-hidden flex items-center justify-center bg-white">
                <img 
                  src="/logo.png"
                  alt="DersMaster Logo"
                  className="h-full w-full object-cover object-center scale-125"
                />
              </div>
              <span className="text-lg font-bold text-gray-900 dark:text-white font-serif">DersMaster</span>
            </div>
            <button
              className="p-2 rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors"
              onClick={() => setMobileOpen(false)}
              aria-label="Menüyü Kapat"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          {/* Mobilde chat ve bildirim butonları */}
          <div className="flex items-center gap-3 px-4 py-4 border-b border-gray-100 dark:border-gray-800">
    
            {isLoggedIn && (
              <>
                {user.profileImageUrl ? (
                  <img
                    src={user.profileImageUrl}
                    alt={user.username}
                    className="h-12 w-12 rounded-full object-cover border-2 border-blue-100 dark:border-blue-900"
                  />
                ) : (
                  <div className="h-12 w-12 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
                    <UserCircleIcon className="h-8 w-8 text-blue-600 dark:text-blue-400" />
                  </div>
                )}
                <div>
                  <p className="font-medium text-gray-900 dark:text-white">{user.username}</p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">{user.role === 'ROLE_ADMIN' ? 'Admin' : user.role === 'ROLE_TEACHER' ? 'Eğitmen' : 'Öğrenci'}</p>
                </div>
              </>
            )}
          </div>

          <nav className="flex-1 flex flex-col gap-2 px-4 py-4">
            {navigation.map((item) =>
              item.name === 'Eğitmen Ol' ? (
                <button
                  key={item.name}
                  onClick={handleRegisterTeacher}
                  className="flex items-center gap-3 px-4 py-3 rounded-lg font-medium bg-gradient-to-r from-purple-500 to-blue-500 text-white"
                >
                  <item.icon className="h-5 w-5 text-white" />
                  {item.name}
                  {item.special && (
                    <SparklesIcon className="h-4 w-4 text-yellow-300 animate-pulse ml-auto" />
                  )}
                </button>
              ) : (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg font-medium transition-all duration-200 ${
                    location.pathname === item.href
                      ? 'bg-blue-100 dark:bg-blue-800/30 text-blue-600 dark:text-blue-300'
                      : item.special 
                        ? 'bg-gradient-to-r from-purple-500 to-blue-500 text-white'
                        : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-blue-600 dark:hover:text-blue-300'
                  }`}
                  onClick={() => setMobileOpen(false)}
                  aria-current={location.pathname === item.href ? 'page' : undefined}
                >
                  <item.icon className={`h-5 w-5 ${
                    location.pathname === item.href
                      ? 'text-blue-600 dark:text-blue-300'
                      : item.special ? 'text-white' : 'text-gray-500 dark:text-gray-400'
                  }`} />
                  {item.name}
                  {item.badge && (
                    <span className="ml-auto px-2 py-0.5 text-xs font-semibold bg-blue-100 dark:bg-blue-900/50 text-blue-600 dark:text-blue-300 rounded-full">
                      {item.badge}
                    </span>
                  )}
                  {item.special && (
                    <SparklesIcon className="h-4 w-4 text-yellow-300 animate-pulse ml-auto" />
                  )}
                </Link>
              )
            )}

            {isLoggedIn ? (
              <div className="mt-4 space-y-2">
                <Link
                  to="/profile"
                  className="flex items-center gap-3 px-4 py-3 rounded-lg font-medium bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                  onClick={() => setMobileOpen(false)}
                >
                  <UserCircleIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                  Profilim
                </Link>
                <button
                  onClick={() => {
                    navigateToDashboard();
                    setMobileOpen(false);
                  }}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-lg font-medium bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 text-left"
                >
                  <ClipboardDocumentListIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                  {user.role === 'ROLE_ADMIN' ? 'Admin Panel' : 'Dashboard'}
                </button>
                <Link
                  to="/settings"
                  className="flex items-center gap-3 px-4 py-3 rounded-lg font-medium bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                  onClick={() => setMobileOpen(false)}
                >
                  <Cog6ToothIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                  Ayarlar
                </Link>
                <button
                  onClick={() => {
                    handleLogout();
                    setMobileOpen(false);
                  }}
                  className="w-full flex items-center gap-3 px-4 py-3 rounded-lg font-medium bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/40 text-left"
                >
                  <ArrowRightOnRectangleIcon className="h-5 w-5 text-red-500 dark:text-red-400" />
                  Çıkış Yap
                </button>
              </div>
            ) : (
              <div className="mt-4 space-y-2">
                <Link
                  to="/login"
                  className="flex items-center gap-3 px-4 py-3 rounded-lg font-medium bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                  onClick={() => setMobileOpen(false)}
                >
                  Giriş Yap
                </Link>
                <Link
                  to="/register"
                  className="flex items-center gap-3 px-4 py-3 rounded-lg font-medium bg-blue-600 text-white hover:bg-blue-700"
                  onClick={() => setMobileOpen(false)}
                >
                  Kayıt Ol
                </Link>
              </div>
            )}

            <button
              onClick={() => {
                toggleDarkMode();
                setMobileOpen(false);
              }}
              className="mt-6 px-4 py-3 rounded-lg flex items-center gap-3 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300"
            >
              {darkMode ? <SunIcon className="h-5 w-5" /> : <MoonIcon className="h-5 w-5" />}
              {darkMode ? 'Açık Mod' : 'Koyu Mod'}
            </button>
          </nav>
        </div>
      </div>
    </>
  );
};

export default Navbar;